// src/utils/tokenService.ts

const DB_NAME = 'NFC_APP_DB';
const DB_VERSION = 2;
const TOKEN_STORE_NAME = 'tokenStore';
const TOKEN_KEY = 'userAuthToken';

/**
 * A simple manager for IndexedDB operations.
 */
class IndexedDBManager {
  private db: IDBDatabase | null = null;

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB error:', request.error);
        reject(new Error('Failed to open IndexedDB.'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(TOKEN_STORE_NAME)) {
          db.createObjectStore(TOKEN_STORE_NAME);
        }
      };
    });
  }

  public async set(value: any): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(TOKEN_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(TOKEN_STORE_NAME);
      const request = store.put(value, TOKEN_KEY);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save token to IndexedDB.'));
    });
  }

  public async get<T>(): Promise<T | null> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(TOKEN_STORE_NAME, 'readonly');
      const store = transaction.objectStore(TOKEN_STORE_NAME);
      const request = store.get(TOKEN_KEY);

      request.onsuccess = () => {
        resolve(request.result || null);
      };
      request.onerror = () => reject(new Error('Failed to retrieve token from IndexedDB.'));
    });
  }

  public async clear(): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(TOKEN_STORE_NAME, 'readwrite');
      const store = transaction.objectStore(TOKEN_STORE_NAME);
      const request = store.delete(TOKEN_KEY);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to delete token from IndexedDB.'));
    });
  }
}

export const idbManager = new IndexedDBManager();

/**
 * Checks if the application is running in PWA standalone mode.
 * @returns {boolean} True if in PWA mode, false otherwise.
 */
export const isPwaMode = (): boolean => {
  // For testing in a browser, you can force PWA mode by adding ?pwa=true to the URL
  const isForcedPwa = new URLSearchParams(window.location.search).get('pwa') === 'true';
  return window.matchMedia('(display-mode: standalone)').matches || isForcedPwa;
};

interface StoredToken {
  token: string;
}

export type TokenFromUrlResult = 'saved' | 'missing' | 'invalid' | 'expired' | 'used';

interface HandleTokenFromUrlOptions {
  cleanUrl?: boolean;
}

interface TokenExchangeFailureBody {
  message?: string;
  reason?: TokenFromUrlResult;
}

interface ExchangeUrlTokenDetailedResult {
  token: string | null;
  reason?: Exclude<TokenFromUrlResult, 'saved' | 'missing'>;
}

const readTokenFromLocalStorage = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    const value = window.localStorage.getItem(TOKEN_KEY);
    return value?.trim() || null;
  } catch {
    return null;
  }
};

const writeTokenToLocalStorage = (token: string): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    window.localStorage.setItem(TOKEN_KEY, token);
    return true;
  } catch {
    return false;
  }
};

const clearTokenFromLocalStorage = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    window.localStorage.removeItem(TOKEN_KEY);
    return true;
  } catch {
    return false;
  }
};

const LEGACY_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isLegacyUuidToken = (value: string): boolean => LEGACY_UUID_PATTERN.test(value);
const shouldUseLegacyUrlTokenFallback = (
  status: number,
  trimmedToken: string
): boolean => (status === 404 || status === 405) && isLegacyUuidToken(trimmedToken);

const buildUrlWithoutToken = (params: URLSearchParams): string => {
  const newSearch = params.toString();
  return `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
};

const normalizeTokenExchangeFailureReason = (
  value: unknown
): Exclude<TokenFromUrlResult, 'saved' | 'missing'> => {
  if (value === 'expired' || value === 'used' || value === 'invalid') {
    return value;
  }

  return 'invalid';
};

const exchangeUrlTokenDetailed = async (
  urlToken: string
): Promise<ExchangeUrlTokenDetailedResult> => {
  const trimmedToken = urlToken.trim();
  if (!trimmedToken) {
    return { token: null };
  }

  if (typeof fetch !== 'function') {
    return { token: trimmedToken };
  }

  try {
    const response = await fetch('/.netlify/functions/notification-link-auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: trimmedToken }),
    });

    if (response.ok) {
      const body = await response.json() as { token?: string };
      const sessionToken = (body.token || '').trim();
      return { token: sessionToken || null };
    }

    if (shouldUseLegacyUrlTokenFallback(response.status, trimmedToken)) {
      return { token: trimmedToken };
    }

    const body = await response.json().catch(() => ({})) as TokenExchangeFailureBody;
    return {
      token: null,
      reason: normalizeTokenExchangeFailureReason(body.reason),
    };
  } catch (error) {
    if (isLegacyUuidToken(trimmedToken)) {
      return { token: trimmedToken };
    }

    return { token: null };
  }
};

const tokenService = {
  /**
   * Saves the authentication token to IndexedDB.
   * @param {string} token The token to save.
   */
  async saveToken(token: string): Promise<void> {
    const normalizedToken = token.trim();
    const didPersistToLocalStorage = writeTokenToLocalStorage(normalizedToken);

    try {
      await idbManager.set({ token: normalizedToken });
    } catch (error) {
      if (didPersistToLocalStorage) {
        console.warn('Failed to save token to IndexedDB, keeping localStorage mirror.', error);
        return;
      }
      throw error;
    }
  },

  /**
   * Retrieves the authentication token from IndexedDB.
   * @returns {Promise<string | null>} The token or null if not found.
   */
  async getToken(): Promise<string | null> {
    const localStorageToken = readTokenFromLocalStorage();

    try {
      const data = await idbManager.get<StoredToken>();
      const indexedDbToken = data?.token?.trim() || null;

      if (indexedDbToken) {
        if (indexedDbToken !== localStorageToken) {
          writeTokenToLocalStorage(indexedDbToken);
        }
        return indexedDbToken;
      }
    } catch (error) {
      if (!localStorageToken) {
        throw error;
      }
    }

    return localStorageToken;
  },

  /**
   * Clears the authentication token from IndexedDB.
   */
  async clearToken(): Promise<void> {
    const didClearLocalStorage = clearTokenFromLocalStorage();

    try {
      await idbManager.clear();
    } catch (error) {
      if (didClearLocalStorage) {
        console.warn('Failed to clear token from IndexedDB, localStorage mirror was cleared.', error);
        return;
      }
      throw error;
    }
  },

  async exchangeUrlToken(urlToken: string): Promise<string | null> {
    const result = await exchangeUrlTokenDetailed(urlToken);
    return result.token;
  },

  /**
   * Checks for a token in the URL, saves it, and cleans the URL.
   * Should be called once when the application initializes.
   */
  async handleTokenFromUrl(options: HandleTokenFromUrlOptions = {}): Promise<TokenFromUrlResult> {
    const shouldCleanUrl = options.cleanUrl ?? true;
    const params = new URLSearchParams(window.location.search);
    const urlToken = (params.get('token') || '').trim();

    if (!urlToken) {
      return 'missing';
    }

    let result: TokenFromUrlResult = 'invalid';
    try {
      const { token: sessionToken, reason } = await exchangeUrlTokenDetailed(urlToken);
      if (sessionToken) {
        await this.saveToken(sessionToken);
        result = 'saved';
      } else if (reason) {
        result = reason;
      }
    } finally {
      if (shouldCleanUrl) {
        params.delete('token');
        window.history.replaceState({}, document.title, buildUrlWithoutToken(params));
      }
    }

    return result;
  },
};

export default tokenService;
